import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Send,
  Eye,
  Settings,
  Target,
  Users,
  Building2,
  Mail,
  BarChart3,
  Calendar,
  Clock,
  Zap,
  CheckCircle,
  AlertTriangle,
  Plus,
  Filter,
  Search,
  Download,
  Upload,
  Sparkles,
  TrendingUp,
  Activity,
  MessageSquare,
  Phone,
  Linkedin,
  Globe
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/textarea';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { cn } from '../../../utils/cn';

// Import our analytics components
import CampaignMetrics from '../../email/analytics/CampaignMetrics';
import RecipientJourney from '../../email/analytics/RecipientJourney';
import { EngagementHeatmap } from '../../email/analytics/EngagementHeatmap';

// Import campaign components
import { CampaignBasicInfo } from '../components/create/CampaignBasicInfo';
import { CampaignTargeting } from '../components/create/CampaignTargeting';
import { CampaignSequence } from '../components/create/CampaignSequence';
import { CampaignTemplates } from '../components/create/CampaignTemplates';
import { CampaignSettings } from '../components/create/CampaignSettings';
import { OutreachTab } from '../components/create/OutreachTab';
import { CompanyTab } from '../components/create/CompanyTab';
import { ContactsTab } from '../components/create/ContactsTab';
import { AnalyticsTab } from '../components/create/AnalyticsTab';
import { CampaignPreview } from '../components/create/CampaignPreview';

interface CampaignData {
  id?: string;
  name: string;
  description: string;
  type: 'email' | 'linkedin' | 'cold_call' | 'sms' | 'multi_channel';
  objective: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  budget: number;
  currency: string;
  startDate: string;
  endDate: string;
  targetCriteria: any;
  sequences: any[];
  templates: any[];
  settings: any;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
}

const steps = [
  { id: 'basic', label: 'Basic Info', icon: Settings },
  { id: 'targeting', label: 'Targeting', icon: Target },
  { id: 'templates', label: 'Templates', icon: Mail },
  { id: 'sequence', label: 'Sequence', icon: Zap },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'preview', label: 'Preview', icon: Eye },
];

const tabs = [
  { id: 'outreach', label: 'Outreach', icon: Send, description: 'Manage campaigns and sequences' },
  { id: 'company', label: 'Company', icon: Building2, description: 'Company insights and data' },
  { id: 'contacts', label: 'Contacts', icon: Users, description: 'Contact management and segmentation' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Performance metrics and insights' },
];

export const CreateCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [activeStep, setActiveStep] = useState('basic');
  const [activeTab, setActiveTab] = useState('outreach');
  const [campaignData, setCampaignData] = useState<CampaignData>({
    name: '',
    description: '',
    type: 'email',
    objective: '',
    priority: 'medium',
    tags: [],
    budget: 0,
    currency: 'USD',
    startDate: '',
    endDate: '',
    targetCriteria: {},
    sequences: [],
    templates: [],
    settings: {},
    status: 'draft'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      loadCampaign(id);
    }
  }, [id, isEditing]);

  const loadCampaign = async (campaignId: string) => {
    setIsLoading(true);
    try {
      // Load campaign data from API
      // const response = await campaignService.getCampaign(campaignId);
      // setCampaignData(response.data);
    } catch (error) {
      console.error('Failed to load campaign:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isDraft = true) => {
    setIsSaving(true);
    try {
      const dataToSave = {
        ...campaignData,
        status: isDraft ? 'draft' : 'active'
      };

      if (isEditing) {
        // Update existing campaign
        // await campaignService.updateCampaign(id, dataToSave);
      } else {
        // Create new campaign
        // await campaignService.createCampaign(dataToSave);
      }

      navigate('/campaigns');
    } catch (error) {
      console.error('Failed to save campaign:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepChange = (step: string) => {
    setActiveStep(step);
  };

  const handleDataChange = (field: string, value: any) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 'basic':
        return (
          <CampaignBasicInfo
            data={campaignData}
            onChange={handleDataChange}
            errors={errors}
          />
        );
      case 'targeting':
        return (
          <CampaignTargeting
            data={campaignData}
            onChange={handleDataChange}
            errors={errors}
          />
        );
      case 'templates':
        return (
          <CampaignTemplates
            data={campaignData}
            onChange={handleDataChange}
            errors={errors}
          />
        );
      case 'sequence':
        return (
          <CampaignSequence
            data={campaignData}
            onChange={handleDataChange}
            errors={errors}
          />
        );
      case 'settings':
        return (
          <CampaignSettings
            data={campaignData}
            onChange={handleDataChange}
            errors={errors}
          />
        );
      case 'preview':
        return (
          <CampaignPreview
            data={campaignData}
            onSave={handleSave}
            isSaving={isSaving}
          />
        );
      default:
        return null;
    }
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'outreach':
        return (
          <OutreachTab
            campaignData={campaignData}
            onDataChange={handleDataChange}
          />
        );
      case 'company':
        return (
          <CompanyTab
            campaignData={campaignData}
            onDataChange={handleDataChange}
          />
        );
      case 'contacts':
        return (
          <ContactsTab
            campaignData={campaignData}
            onDataChange={handleDataChange}
          />
        );
      case 'analytics':
        return (
          <AnalyticsTab
            campaignData={campaignData}
            onDataChange={handleDataChange}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Loading campaign...
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Please wait while we fetch your campaign data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/campaigns')}
                className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Campaigns
              </Button>
              <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700" />
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {isEditing ? 'Edit Campaign' : 'Create New Campaign'}
                </h1>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {isEditing ? 'Update your campaign settings and content' : 'Set up a new outreach campaign with advanced targeting and analytics'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => handleSave(true)}
                disabled={isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </Button>
              <Button
                variant="gradient"
                onClick={() => handleSave(false)}
                disabled={isSaving}
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isEditing ? 'Update Campaign' : 'Launch Campaign'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar - Steps Navigation */}
          <div className="col-span-3">
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-6">
                Campaign Setup
              </h3>
              <nav className="space-y-2">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = activeStep === step.id;
                  const isCompleted = steps.findIndex(s => s.id === activeStep) > index;
                  
                  return (
                    <button
                      key={step.id}
                      onClick={() => handleStepChange(step.id)}
                      className={cn(
                        "w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200",
                        isActive
                          ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                          : isCompleted
                          ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30"
                          : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100"
                      )}
                    >
                      <div className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                        isActive
                          ? "bg-primary-600 text-white"
                          : isCompleted
                          ? "bg-green-600 text-white"
                          : "bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400"
                      )}>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                      </div>
                      <span className="font-medium">{step.label}</span>
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <Card className="p-8">
              {/* Tab Navigation */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
                <TabsList className="grid w-full grid-cols-4 mb-8">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className="flex items-center space-x-2 px-6 py-3"
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {getTabContent()}
                  </motion.div>
                </AnimatePresence>
              </Tabs>

              {/* Step Content */}
              <div className="border-t border-neutral-200 dark:border-neutral-800 pt-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {getStepContent()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 